import { useEffect, useRef, useState } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import {
  T, Icon, stitchCard, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn,
  stitchSectionLabel, stitchTooltipSlotProps,
} from '../ProjectOverview/parts/stitch';
import TaskItem from '../ProjectOverview/parts/TaskItem';
import { useProjectData } from '../ProjectOverview/ProjectDataContext';
import ConceptTrainer from './ConceptTrainer';
import DossierBlok from './DossierBlok';
import FaseTemplates from './FaseTemplates';
import { MailKoppelBox, leesBriefingBestand, bewaarKlantbriefing } from './bronnenKoppelen';
import GenResultDialog from './GenResultDialog';
import DraaidagKoppelDialog from './DraaidagKoppelDialog';
import BriefingCheckStatus from './BriefingCheckStatus';
import { volgBriefingChecks } from './briefingPolling';
import { maakKlantMail, mistVergelijking } from './briefingMail';
import { FLOW_PHASES, PANEL_LABELS, STATUS_STYLES, buildToolUrl } from '../../config/projectFlow';

/**
 * Het werkvlak van de geselecteerde fase: status mét bewijs, de taken van deze
 * fase, het briefingblok (de iPad-test — lege velden staan er als "niet
 * ingevuld"), de documentgenerator, de tools en de detailpanelen mét aantallen.
 */
function StagePanel({ phase, project, onOpenPaneel, onOpenDialog, onFlowChanged }) {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const {
    tasks, deliverables, notes, transcripts, quitclaims, draaidagen, callsheets,
    frameioVideos, qaReviews, quoteLines, setSelectedTask, handleToggleComplete,
    loadData,
  } = useProjectData();
  const meta = FLOW_PHASES[phase?.phase_key] || { tools: [], panels: [] };
  const stijl = STATUS_STYLES[phase?.status] || STATUS_STYLES.todo;
  const projectRecId = project?.airtable_id || project?.id;
  const [mailKoppelOpen, setMailKoppelOpen] = useState(false);
  const [draaidagKoppelOpen, setDraaidagKoppelOpen] = useState(false);

  // Mail/notulen/meeting koppelen: MailKoppelBox (bronnenKoppelen, gedeeld met de
  // projectstrook op de callsheet). Na koppelen herladen, anders bleef het notitiepaneel
  // op de oude lijst staan (Joris, 11 sep 2026).

  // Aantallen op de detail-chips: "Draaidagen (2)" leest sneller dan doorklikken.
  const PANEL_COUNTS = {
    taken: tasks.length, deliverables: deliverables.length, notes: notes.length,
    meetings: transcripts.length, quitclaims: quitclaims.length,
    draaidagen: draaidagen.length, callsheets: callsheets.length,
    frameio: frameioVideos.length, qa: qaReviews.length,
    offerteregels: quoteLines?.count ?? null, activiteit: null, correspondentie: null,
  };

  // De taken van deze fase — zelfde type-bril als de status-afleiding.
  // Gearchiveerde taken (o.a. vervallen draaidag-OPTIES) horen hier niet meer.
  const faseTaken = (meta.taskTypes || []).length
    ? tasks.filter((t) => (meta.taskTypes || []).includes(t.fields?.Type)
        && t.fields?.PrioNr !== 'Gearchiveerd')
    : [];
  const [toonAfgeronde, setToonAfgeronde] = useState(false);
  // Inklappen per deliverable (Joris, 10 sep 2026: een fase met 11 taken duwt de
  // rest van het paneel uit beeld). De keuze staat per project én fase in
  // localStorage, zodat je bij terugkomen niet opnieuw zit te klappen.
  const klapSleutel = `projectflow-taken-dicht:${projectRecId || 'x'}:${phase?.phase_key || 'x'}`;
  const [dichteGroepen, setDichteGroepen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(klapSleutel) || '[]')); }
    catch { return new Set(); }
  });
  const bewaarDicht = (volgende) => {
    setDichteGroepen(volgende);
    try { localStorage.setItem(klapSleutel, JSON.stringify([...volgende])); } catch { /* privémodus */ }
  };
  // Andere fase = andere sleutel; zonder dit blijft de stand van de vorige fase hangen.
  useEffect(() => {
    try { setDichteGroepen(new Set(JSON.parse(localStorage.getItem(klapSleutel) || '[]'))); }
    catch { setDichteGroepen(new Set()); }
  }, [klapSleutel]);
  const wisselGroep = (key) => {
    const volgende = new Set(dichteGroepen);
    if (volgende.has(key)) volgende.delete(key); else volgende.add(key);
    bewaarDicht(volgende);
  };
  // Bulk: alle open fase-taken in één keer afronden (oude taken opruimen).
  const [allesAfvinken, setAllesAfvinken] = useState(false);
  const vinkAllesAf = async () => {
    if (!(await bevestig({ titel: `Alle ${openTaken.length} open taken afvinken?`, tekst: 'Alle open taken in deze fase worden als afgerond gemarkeerd.', knop: 'Afvinken' }))) return;
    setAllesAfvinken(true);
    let gelukt = 0;
    try {
      for (const t of openTaken) {
        // Bulk: geen undo-toast per taak.
        try { await handleToggleComplete(t, true, { metOngedaan: false }); gelukt += 1; }
        catch { /* doortellen; melding volgt hieronder */ }
      }
      if (gelukt === openTaken.length) toast.success(`${gelukt} taken afgevinkt`);
      else toast.error(`${gelukt}/${openTaken.length} afgevinkt — probeer de rest opnieuw`);
    } finally { setAllesAfvinken(false); }
  };
  // Chronologisch: je moet de timing voelen. Zonder datum → onderaan.
  const taakDatum = (t) => t.fields?.StartDateUitvoerder || t.fields?.EndDateUitvoerder || t.fields?.Deadline || '9999';
  const opDatum = (arr) => [...arr].sort((a, b) => String(taakDatum(a)).localeCompare(String(taakDatum(b))));
  const openTaken = opDatum(faseTaken.filter((t) => t.fields?.Afgerond !== true));
  const klareTaken = opDatum(faseTaken.filter((t) => t.fields?.Afgerond === true));
  // Groeperen per deliverable (9487-02, 9487-03, …) — daar zitten de
  // afhankelijkheden: V1 → V2 → DEF binnen dezelfde deliverable.
  // Let op de veldnaam: de spiegel noemt hem 'Naam van Deliverable'. Met de
  // oude gok stond er boven elke groep letterlijk "Deliverable" (Joris, 11 sep).
  const delNaam = new Map((deliverables || []).map((d) => [
    d.id,
    d.fields?.['Naam van Deliverable'] || d.fields?.Deliverable || d.fields?.Naam
      || d.name || 'Deliverable',
  ]));
  const taakGroepen = (() => {
    const per = new Map();
    openTaken.forEach((t) => {
      const key = (t.fields?.Deliverable || [])[0] || '_overig';
      if (!per.has(key)) per.set(key, []);
      per.get(key).push(t);
    });
    return [...per.entries()].map(([key, taken]) => ({
      key, taken, naam: key === '_overig' ? 'Overige taken' : (delNaam.get(key) || 'Deliverable'),
    }));
  })();

  const allesDicht = taakGroepen.length > 0 && taakGroepen.every((g) => dichteGroepen.has(g.key));

  // Documentgenerator (debrief → markdown-dialog, concept → PowerPoint-download)
  const [genBusy, setGenBusy] = useState(false);
  const [trainerOpen, setTrainerOpen] = useState(false);
  const [genResult, setGenResult] = useState(null); // { titel, markdown }
  const [checkBusy, setCheckBusy] = useState(false);
  // { id, naam, notities } — onderwerp hernoemen en notities bij een klantbriefing.
  const [briefingEdit, setBriefingEdit] = useState(null);
  const briefingFileRef = useRef(null);

  // Klantbriefing: eerst BEWAREN bij het project, dan analyseren. Andersom kost
  // een mislukte AI-call je het document; nu blijft de briefing staan en kun je
  // de analyse later overdoen. Sinds 28 sep loopt de AI-check op de achtergrond:
  // bewaren is meteen klaar, daarna pollen we de lijst tot de check klaar is.
  const [plakOpen, setPlakOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [plakTekst, setPlakTekst] = useState('');
  const [briefingDocs, setBriefingDocs] = useState([]);
  const [briefingContact, setBriefingContact] = useState(null);
  const pollStop = useRef(null);

  const haalBriefingDocs = async () => {
    const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    setBriefingContact(d?.contact || null);
    return d?.briefings || [];
  };
  const laadBriefingDocs = async () => {
    if (!projectRecId) return;
    try { setBriefingDocs(await haalBriefingDocs()); } catch { /* lijst is aanvullend */ }
  };
  // Ook buiten de opstartfase laden: het Airtable-briefingformulier is vaak leeg
  // en dan is "geen briefing gekoppeld" verwarrend als er wél een klantbriefing
  // bij het project staat.
  useEffect(() => {
    laadBriefingDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectRecId]);

  // Loopt er een AI-check (net geüpload, of pagina opnieuw geopend)? Dan elke 5 s
  // de lijst opnieuw ophalen, hooguit 3 minuten.
  useEffect(() => {
    const bezig = briefingDocs.filter((d) => d.analyse_status === 'bezig').map((d) => d.id);
    if (!bezig.length || pollStop.current || !projectRecId) return;
    pollStop.current = volgBriefingChecks({
      haalOp: haalBriefingDocs,
      ids: bezig,
      onResultaat: setBriefingDocs,
      onKlaar: (doc) => {
        const naam = doc.bestandsnaam || 'klantbriefing';
        if (doc.analyse_status === 'klaar') toast.success(`AI-check klaar — ${naam}. Open hem via ‘Briefing-check’.`);
        else toast.error(`AI-check mislukt — ${naam}${doc.analyse_fout ? ` (${doc.analyse_fout})` : ''}. Probeer het opnieuw.`);
      },
      onTimeout: () => toast('De AI-check duurt langer dan verwacht — kijk straks nog eens.', { icon: '⏳' }),
      onEinde: () => { pollStop.current = null; },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefingDocs, projectRecId]);
  useEffect(() => () => pollStop.current?.(), []);

  const openBriefingCheck = (doc) => {
    const projectNaam = project?.fields?.Projectnaam || briefingContact?.project_naam || '';
    setGenResult({
      titel: `Briefing-check — ${doc.bestandsnaam || 'klantbriefing'}`,
      markdown: doc.analyse_markdown || '',
      hint: mistVergelijking(doc.analyse_markdown)
        ? 'Deze check is van vóór de vergelijking met offerte en deliverables. Laat hem opnieuw lopen om die erbij te krijgen.'
        : null,
      opnieuw: mistVergelijking(doc.analyse_markdown)
        ? () => { setGenResult(null); heranalyseerBriefingDoc(doc.id); }
        : null,
      klantMail: maakKlantMail({
        mailKlant: doc.mail_klant,
        projectNaam,
        afzender: accounts?.[0]?.name || '',
        contactNaam: briefingContact?.naam,
      }),
      klantEmail: briefingContact?.email || null,
    });
  };

  const bewaarBriefingTekst = async (text, label, bron) => {
    if (!String(text || '').trim() || checkBusy) return;
    setCheckBusy(true);
    try {
      await bewaarKlantbriefing(projectRecId, { tekst: text, bestandsnaam: label, bron });
      setPlakOpen(false);
      setPlakTekst('');
      toast.success('Opgeslagen · AI-check loopt…');
      await laadBriefingDocs();
    } catch (e) { toast.error(`Briefing opslaan mislukt: ${e.message}`); }
    finally { setCheckBusy(false); }
  };

  const uploadKlantBriefing = async (file) => {
    if (!file || checkBusy) return;
    setCheckBusy(true);
    try {
      const text = await leesBriefingBestand(file);
      setCheckBusy(false);
      await bewaarBriefingTekst(text, file.name, 'upload');
    } catch (e) { setCheckBusy(false); toast.error(`Briefing opslaan mislukt: ${e.message}`); }
  };

  // Opnieuw laten lezen: ook op de achtergrond, dezelfde polling als bij uploaden.
  const heranalyseerBriefingDoc = async (id) => {
    if (checkBusy) return;
    setCheckBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings/${id}/heranalyseer`, { method: 'POST' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      toast.success('AI-check loopt opnieuw…');
      await laadBriefingDocs();
    } catch (e) { toast.error(`Opnieuw lezen mislukt: ${e.message}`); }
    finally { setCheckBusy(false); }
  };

  const bewaarBriefingWijziging = async (id) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bestandsnaam: briefingEdit?.naam || '', notities: briefingEdit?.notities || '' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setBriefingEdit(null);
      await laadBriefingDocs();
      toast.success('Briefing bijgewerkt');
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); }
  };

  const verwijderBriefingDoc = async (id) => {
    if (!(await bevestig({ titel: 'Briefing verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await laadBriefingDocs();
      toast.success('Briefing verwijderd');
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
  };

  const runGenerator = async () => {
    if (!meta.generator || genBusy) return;
    setGenBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/document`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: meta.generator.kind }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${r.status}`);
      }
      if (meta.generator.output === 'pptx') {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `concept-${project?.fields?.Projectnummer || 'project'}.pptx`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Conceptpresentatie gedownload');
      } else {
        const d = await r.json();
        setGenResult({ titel: 'Debrief — gegenereerd door Fable 5', markdown: d.markdown || '' });
      }
    } catch (e) { toast.error(`Genereren mislukt: ${e.message}`); }
    finally { setGenBusy(false); }
  };

  if (!phase) return null;

  return (
    <Box sx={{ ...stitchCard, p: { xs: 2, md: 3 }, '&:hover': { boxShadow: T.shadow, transform: 'none' } }}
      onDragOver={phase.phase_key === 'opstartfase' ? (e) => { e.preventDefault(); setDragActive(true); } : undefined}>
      {/* Kop: fase + status mét bewijs */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '0.6rem', bgcolor: T.tertiaryContainer,
          color: T.tertiary, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <Icon name={meta.icon || 'flag'} size={22} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Rust: de fase-uitleg staat niet meer als losse regel maar achter mouse-over */}
            <Tooltip title={meta.beschrijving || ''} slotProps={stitchTooltipSlotProps}>
              <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.15rem', color: T.text, cursor: 'default' }}>
                {phase.label}
              </Typography>
            </Tooltip>
            <Chip size="small" label={stijl.label}
              sx={{ height: 22, fontWeight: 800, fontSize: 11, bgcolor: stijl.bg, color: stijl.color }} />
          </Box>
          {/* Rust: één bewijsregel; de rest + bron achter het info-icoon */}
          {(phase.evidence || []).length > 0 && (
            <Tooltip slotProps={stitchTooltipSlotProps}
              title={[...(phase.evidence || []), `bron: ${phase.status_source}`].join(' · ')}>
              <Typography variant="caption" sx={{ mt: 0.5, fontFamily: T.fontBody, color: T.secondary,
                display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                <Icon name="info" size={13} sx={{ color: T.outlineVariant }} />
                {phase.evidence[0]}
                {(phase.evidence.length > 1) && (
                  <Typography component="span" variant="caption" sx={{ color: T.outlineVariant }}>
                    +{phase.evidence.length - 1}
                  </Typography>
                )}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {/* Documentgenerator van deze fase (+ trainen bij het concept) */}
        {meta.generator && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
            {phase.phase_key === 'opstartfase' && (
              <>
                <input ref={briefingFileRef} type="file" hidden accept=".pdf,.docx,.pptx,.txt,.md"
                  onChange={(e) => { uploadKlantBriefing(e.target.files?.[0]); e.target.value = ''; }} />
                <Tooltip title="Bewaart het klantdocument bij dit project en checkt wat er ontbreekt" slotProps={stitchTooltipSlotProps}>
                  <Box component="span" onClick={() => !checkBusy && briefingFileRef.current?.click()}
                    sx={{ ...stitchOutlineBtn, opacity: checkBusy ? 0.6 : 1 }}>
                    {checkBusy ? <CircularProgress size={15} /> : <Icon name="upload_file" size={16} />}
                    {checkBusy ? 'Bewaren…' : 'Klantbriefing uploaden'}
                  </Box>
                </Tooltip>
                <Tooltip title="Mail of tekst plakken i.p.v. bestand" slotProps={stitchTooltipSlotProps}>
                  <Box component="span" onClick={() => !checkBusy && setPlakOpen(true)}
                    sx={{ ...stitchOutlineBtn, px: 1.25 }}>
                    <Icon name="content_paste" size={16} />
                  </Box>
                </Tooltip>
              </>
            )}
            {meta.generator.kind === 'concept' && (
              <Tooltip title="Voed de conceptmachine met voorbeelden en richtlijnen" slotProps={stitchTooltipSlotProps}>
                <Box component="span" onClick={() => setTrainerOpen(true)} sx={stitchOutlineBtn}>
                  <Icon name="school" size={16} /> Trainen
                </Box>
              </Tooltip>
            )}
            <Tooltip title={meta.generator.uitleg} slotProps={stitchTooltipSlotProps}>
              <Box component="span" onClick={runGenerator}
                sx={{ ...stitchGradientBtn, opacity: genBusy ? 0.6 : 1 }}>
                {genBusy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Icon name="auto_awesome" size={17} />}
                {genBusy ? 'Fable 5 schrijft…' : meta.generator.label}
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Taken van deze fase */}
      {(meta.taskTypes || []).length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography sx={{ ...stitchSectionLabel, mb: 0 }}>
              Taken in deze fase ({klareTaken.length}/{faseTaken.length} af)
            </Typography>
            {taakGroepen.length > 0 && (
              <Tooltip title={allesDicht ? 'Alle groepen uitklappen' : 'Alle groepen inklappen'} slotProps={stitchTooltipSlotProps}>
                <Box component="button" onClick={() => bewaarDicht(allesDicht ? new Set() : new Set(taakGroepen.map((g) => g.key)))}
                  sx={{ ...stitchOutlineBtn, py: 0.25, px: 1, fontSize: '0.7rem', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Icon name={allesDicht ? 'unfold_more' : 'unfold_less'} size={13} />
                  {allesDicht ? 'Uitklappen' : 'Inklappen'}
                </Box>
              </Tooltip>
            )}
            {openTaken.length > 1 && (
              <Tooltip title="Alle open taken in deze fase in één keer afronden — handig om oude taken op te ruimen" slotProps={stitchTooltipSlotProps}>
                <Box component="button" onClick={vinkAllesAf} disabled={allesAfvinken}
                  sx={{ ...stitchOutlineBtn, py: 0.25, px: 1, fontSize: '0.7rem', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, opacity: allesAfvinken ? 0.6 : 1 }}>
                  {allesAfvinken ? <CircularProgress size={11} /> : <Icon name="done_all" size={13} />}
                  Alles afvinken
                </Box>
              </Tooltip>
            )}
          </Box>
          {faseTaken.length === 0 ? (
            <Tooltip title="Genereer projecttaken via het Taken-paneel of 'Projecttaken genereren'" slotProps={stitchTooltipSlotProps}>
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, cursor: 'default' }}>
                Nog geen taken
              </Typography>
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {taakGroepen.map((groep) => {
                const dicht = dichteGroepen.has(groep.key);
                return (
                  <Box key={groep.key}>
                    <Box component="button" onClick={() => wisselGroep(groep.key)}
                      aria-expanded={!dicht}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, mt: 0.5, p: 0,
                        border: 0, bgcolor: 'transparent', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                      <Icon name={dicht ? 'chevron_right' : 'expand_more'} size={15} />
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, fontWeight: 700 }}>
                        {groep.naam}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, fontWeight: 700 }}>
                        ({groep.taken.length})
                      </Typography>
                    </Box>
                    {!dicht && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {groep.taken.map((t) => (
                          <TaskItem key={t.id} task={t} compact onClick={() => setSelectedTask(t)} onToggleComplete={handleToggleComplete} />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
              {klareTaken.length > 0 && (
                <Typography variant="caption" onClick={() => setToonAfgeronde((v) => !v)}
                  sx={{ fontFamily: T.fontBody, color: T.primary, cursor: 'pointer', fontWeight: 700 }}>
                  {toonAfgeronde ? 'Verberg afgeronde taken' : `Toon ${klareTaken.length} afgeronde taken`}
                </Typography>
              )}
              {toonAfgeronde && klareTaken.map((t) => (
                <TaskItem key={t.id} task={t} compact onClick={() => setSelectedTask(t)} onToggleComplete={handleToggleComplete} />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Tools van deze fase — altijd renderen: de 'E-mail koppelen'-chip
          hoort in elke fase beschikbaar te zijn, ook als de fase zelf geen
          tools heeft (afronding). */}
      {(
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>Gereedschap</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {(meta.dialogActions || []).map((actie) => (
              <Box key={actie.key} onClick={() => onOpenDialog?.(actie.key)} sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.9,
                borderRadius: '0.6rem', cursor: 'pointer', bgcolor: T.tertiaryContainer + '55',
                fontFamily: T.font, fontSize: '0.8rem', fontWeight: 700, color: T.tertiary,
                transition: 'all 0.15s ease', '&:hover': { bgcolor: T.tertiaryContainer } }}>
                <Icon name={actie.icon} size={17} />
                {actie.label}
              </Box>
            ))}
            <Box onClick={() => setMailKoppelOpen((v) => !v)} sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.9,
              borderRadius: '0.6rem', cursor: 'pointer',
              bgcolor: mailKoppelOpen ? T.tertiaryContainer : T.surfaceLow,
              fontFamily: T.font, fontSize: '0.8rem', fontWeight: 700, color: T.text,
              transition: 'all 0.15s ease', '&:hover': { bgcolor: T.surfaceHigh } }}>
              <Icon name="attach_email" size={17} sx={{ color: T.tertiary }} />
              E-mail koppelen
            </Box>
            {/* Productie: draaidagen zonder project alsnog aan dit project hangen —
                anders blijft de kaart op "geen draaidagen gekoppeld" staan. */}
            {phase.phase_key === 'productie' && (
              <Box component="button" type="button" onClick={() => setDraaidagKoppelOpen(true)} sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.9, border: 0,
                borderRadius: '0.6rem', cursor: 'pointer', bgcolor: T.surfaceLow,
                fontFamily: T.font, fontSize: '0.8rem', fontWeight: 700, color: T.text,
                transition: 'all 0.15s ease', '&:hover': { bgcolor: T.surfaceHigh } }}>
                <Box component="span" sx={{ position: 'relative', display: 'inline-flex', color: T.tertiary }}>
                  <Icon name="calendar_month" size={17} />
                  <Box component="span" sx={{ position: 'absolute', right: -5, bottom: -4, display: 'inline-flex',
                    bgcolor: T.surfaceLow, borderRadius: '9999px' }}>
                    <Icon name="link" size={11} />
                  </Box>
                </Box>
                Draaidagen koppelen
              </Box>
            )}
            {meta.tools.map((tool) => (
              <Tooltip key={tool.path + tool.label} slotProps={stitchTooltipSlotProps}
                title={tool.supportsContext ? 'Opent met dit project' : '⚑ Opent zonder projectcontext'}>
                <Box onClick={() => navigate(buildToolUrl(tool, project))} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.9,
                  borderRadius: '0.6rem', cursor: 'pointer', bgcolor: T.surfaceLow,
                  fontFamily: T.font, fontSize: '0.8rem', fontWeight: 700, color: T.text,
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: T.surfaceHigh },
                }}>
                  <Icon name={tool.icon} size={17} sx={{ color: T.tertiary }} />
                  {tool.label}
                  {!tool.supportsContext && (
                    <Typography component="span" sx={{ fontSize: 11, color: T.amber, fontWeight: 800 }}>⚑</Typography>
                  )}
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      {/* Mail/notulen aan het project hangen — zoeker uit de offerte-generator */}
      {mailKoppelOpen && (
        <MailKoppelBox projectRecId={projectRecId} onGekoppeld={loadData}
          projectNumber={project?.fields?.Projectnummer ? String(project.fields.Projectnummer) : (project?.projectNumber || '')}
          projectName={project?.fields?.Projectnaam || project?.projectName || ''}
          contactEmail={(() => { const em = project?.fields?.emailCP || []; return Array.isArray(em) ? (em[0] || '') : String(em || ''); })()} />
      )}

      {/* Documenten van deze fase: het ingevulde document voorop, templates als startpunt */}
      <FaseTemplates fase={phase.phase_key} projectRecId={projectRecId}
        projectNumber={project?.fields?.Projectnummer || ''} />

      {/* Klantbriefing er direct in slepen — het grote vak alleen zolang er nog
          geen briefing is (of tijdens het slepen zelf); daarna volstaat de knop. */}
      {phase.phase_key === 'opstartfase' && (briefingDocs.length === 0 || dragActive) && (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragActive(false);
            const file = e.dataTransfer?.files?.[0];
            if (file) uploadKlantBriefing(file);
          }}
          onClick={() => !checkBusy && briefingFileRef.current?.click()}
          sx={{
            mb: 2.5, p: 2, borderRadius: '0.75rem', cursor: 'pointer', textAlign: 'center',
            border: `2px dashed ${dragActive ? T.tertiary : T.outlineVariant}`,
            bgcolor: dragActive ? T.tertiaryContainer + '40' : 'transparent',
            transition: 'all 0.15s ease', '&:hover': { borderColor: T.tertiary },
          }}>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: dragActive ? T.tertiary : T.outline,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
            {checkBusy ? <CircularProgress size={14} /> : <Icon name="place_item" size={17} />}
            {checkBusy ? 'Bewaren…' : 'Sleep de klantbriefing hierheen of klik'}
          </Typography>
        </Box>
      )}

      {/* Wat de klant zelf aanleverde — bewaard bij het project, los van ons
          Airtable-briefingformulier. */}
      {briefingDocs.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>
            Klantbriefing bij dit project ({briefingDocs.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {briefingDocs.map((doc) => {
              // Sleutels met een liggend streepje zijn intern (bv. _notities) en
              // horen niet tussen de kernveld-chips.
              const velden = Object.entries(doc.velden || {}).filter(([k, v]) => v && !k.startsWith('_'));
              const notities = doc.velden?._notities || '';
              const inBewerking = briefingEdit?.id === doc.id;
              return (
                <Box key={doc.id} sx={{ border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.6rem', p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Icon name={doc.bron === 'plak' ? 'content_paste' : 'description'} size={17} sx={{ color: T.tertiary }} />
                    <Tooltip title="Klik om het onderwerp te wijzigen" slotProps={stitchTooltipSlotProps}>
                      <Typography
                        onClick={() => setBriefingEdit({ id: doc.id, naam: doc.bestandsnaam || '', notities })}
                        sx={{
                          fontFamily: T.font, fontWeight: 800, fontSize: '0.85rem', color: T.text,
                          cursor: 'pointer', borderBottom: `1px dashed ${T.outlineVariant}`,
                        }}>
                        {doc.bestandsnaam || 'Klantbriefing'}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant }}>
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString('nl-NL') : ''}
                      {doc.aangemaakt_door ? ` · ${doc.aangemaakt_door}` : ''}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <BriefingCheckStatus doc={doc} bezigMetKnop={checkBusy}
                      onBekijk={() => openBriefingCheck(doc)}
                      onOpnieuw={() => heranalyseerBriefingDoc(doc.id)} />
                    <Tooltip title="Briefing verwijderen bij dit project" slotProps={stitchTooltipSlotProps}>
                      <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, px: 1 }}
                        onClick={() => verwijderBriefingDoc(doc.id)}>
                        <Icon name="delete" size={15} />
                      </Box>
                    </Tooltip>
                  </Box>
                  {velden.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {velden.map(([naam, waarde]) => (
                        <Tooltip key={naam} title={String(waarde)} slotProps={stitchTooltipSlotProps}>
                          <Chip size="small" label={naam.replace(/_/g, ' ')}
                            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: T.tertiaryContainer + '55', color: T.tertiary }} />
                        </Tooltip>
                      ))}
                    </Box>
                  )}

                  {/* De briefing en de notities die je erbij maakt zijn twee dingen —
                      die van de klant, en die van jezelf tijdens het gesprek. */}
                  {inBewerking ? (
                    <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField size="small" label="Onderwerp" value={briefingEdit.naam}
                        onChange={(e) => setBriefingEdit((b) => ({ ...b, naam: e.target.value }))}
                        placeholder="bv. Briefing gesprek 17 aug" fullWidth />
                      <TextField size="small" label="Notities bij de briefing" multiline minRows={3}
                        value={briefingEdit.notities}
                        onChange={(e) => setBriefingEdit((b) => ({ ...b, notities: e.target.value }))}
                        placeholder="Wat er tijdens het gesprek is gezegd en niet in het document staat…" fullWidth />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}
                          onClick={() => bewaarBriefingWijziging(doc.id)}>
                          <Icon name="save" size={15} /> Bewaren
                        </Box>
                        <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}
                          onClick={() => setBriefingEdit(null)}>Annuleren</Box>
                      </Box>
                    </Box>
                  ) : notities ? (
                    <Box sx={{ mt: 1.25, p: 1, borderRadius: '0.5rem', bgcolor: T.surfaceHigh + '55' }}>
                      <Typography variant="caption" sx={{ ...stitchSectionLabel, display: 'block', mb: 0.25 }}>
                        Notities
                      </Typography>
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text, whiteSpace: 'pre-wrap' }}>
                        {notities}
                      </Typography>
                      <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.3, px: 1, fontSize: '0.72rem', mt: 0.75 }}
                        onClick={() => setBriefingEdit({ id: doc.id, naam: doc.bestandsnaam || '', notities })}>
                        <Icon name="edit" size={13} /> Bewerken
                      </Box>
                    </Box>
                  ) : (
                    <Box component="span" sx={{ ...stitchOutlineBtn, py: 0.3, px: 1, fontSize: '0.72rem', mt: 1 }}
                      onClick={() => setBriefingEdit({ id: doc.id, naam: doc.bestandsnaam || '', notities: '' })}>
                      <Icon name="add_notes" size={13} /> Notities toevoegen
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Projectdossier — vervangt de Airtable-briefing-doorgifte (die tabel is
          bevroren: 7 records ooit, 71 velden nooit ingevuld). Kern op elke fase;
          op Productie het draaidagblok (de iPad-test), en het live offerte-blok
          bij offerte/opstart. */}
      <DossierBlok
        projectRecId={projectRecId}
        projectNumber={project?.fields?.Projectnummer || project?.projectNumber}
        sectie={phase.phase_key === 'productie' ? 'productie' : 'kern'}
        toonVast={['offertefase', 'opstartfase'].includes(phase.phase_key)}
        standaardIngeklapt={!['opstartfase', 'offertefase', 'concept'].includes(phase.phase_key)}
      />

      {/* Detailpanelen (de oude tabbladen — één klik ver, mét aantallen) */}
      {meta.panels.length > 0 && (
        <Box>
          <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>Details</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {meta.panels.map((key) => (
              <Box key={key} onClick={() => onOpenPaneel(key)} sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.6,
                borderRadius: '9999px', cursor: 'pointer',
                border: `1.5px solid ${T.outlineVariant}`, color: T.primary,
                fontFamily: T.font, fontSize: '0.75rem', fontWeight: 700,
                '&:hover': { borderColor: T.primary, bgcolor: T.surfaceLow },
              }}>
                {PANEL_LABELS[key] || key}
                {PANEL_COUNTS[key] !== null && PANEL_COUNTS[key] !== undefined && ` (${PANEL_COUNTS[key]})`}
                <Icon name="open_in_full" size={13} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <ConceptTrainer open={trainerOpen} onClose={() => setTrainerOpen(false)} />

      {phase.phase_key === 'productie' && (
        <DraaidagKoppelDialog open={draaidagKoppelOpen} onClose={() => setDraaidagKoppelOpen(false)}
          projectRecId={projectRecId}
          onGewijzigd={async () => { await Promise.all([onFlowChanged?.(), loadData?.()]); }} />
      )}

      {/* Mail/tekst plakken voor de briefing-check */}
      <Dialog open={plakOpen} onClose={() => setPlakOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: stitchDialogPaper }}>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>Klantbriefing plakken</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 1 }}>
            Plak de mail of tekst van de klant. Die wordt bewaard bij dit project en daarna
            gelezen: wat staat erin, wat ontbreekt (deliverables, lengtes, ondertiteling,
            kanalen/formaten) en wat vraag je na.
          </Typography>
          <textarea value={plakTekst} onChange={(e) => setPlakTekst(e.target.value)} rows={12}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6,
              border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, resize: 'vertical' }}
            placeholder="Plak hier de mail of briefing-tekst…" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box component="span" onClick={() => setPlakOpen(false)} sx={stitchOutlineBtn}>Annuleren</Box>
          <Box component="span" onClick={() => bewaarBriefingTekst(plakTekst, 'geplakte tekst', 'plak')}
            sx={{ ...stitchGradientBtn, opacity: (!plakTekst.trim() || checkBusy) ? 0.5 : 1 }}>
            {checkBusy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <Icon name="auto_awesome" size={16} />}
            Bewaren bij project
          </Box>
        </DialogActions>
      </Dialog>

      {/* Debrief-resultaat */}
      <GenResultDialog result={genResult} onClose={() => setGenResult(null)} projectRecId={projectRecId} />
    </Box>
  );
}

export default StagePanel;
